/* eslint-disable no-param-reassign */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import uuid from 'react-uuid';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, LedgerTable } from 'components';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { cellsParser } from 'components/LedgerTable/utilities/ledgerParser';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  CashDistributionLedgerAcquisition,
  ShareSaleReport,
  ShareSaleReportRow,
} from 'pages/CapTable/fund-ownership/components/cash-distribution/CashDistributionLedger.props';
import {
  AMOUNT_ALIAS,
  COST_BASIS_ALIAS,
  PROCEEDS_ALIAS,
  SALE_DATE_ALIAS,
  SHARES_ALIAS,
  SOLD_PERCENT_ALIAS,
} from 'pages/CapTable/fund-ownership/components/shares-ledger/constants';
import useExchangeRateMap from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useExchangeRateMap';
import { applyPreFxAnnotation } from 'pages/CapTable/fund-ownership/components/shares-ledger/utilities/applyPreFxAnnotation';
import { FundOwnershipCell } from 'pages/CapTable/fund-ownership/types';
import { colConfig } from './colConfig';
import { SoldTableProps } from './SoldTable.props';
import conditions from '../../conditions';

const preEmptyRow: ShareSaleReport = {
  sale_date: null,
  purchase_date: null,
  acquisition_ref: null,
};

const SoldTable = ({
  cell,
  doFullValidation,
  setIsValid,
  updateValidationStatus,
  isDisabled,
  modeledAfterConvertibleNote,
  fundCurrency,
  isDifferentCurrency,
  selectedMeasurementDate,
  format,
  isLoanNote,
  acquisitionsData,
  setAcquisitionsData,
  reverseParsedAcquisitions,
  soldCells,
  setSoldCells,
  sharesMaxDecimalPlaces,
}: SoldTableProps) => {
  const emptyRow = useMemo((): ShareSaleReportRow => {
    if (modeledAfterConvertibleNote) {
      if (isLoanNote) {
        return {
          ...preEmptyRow,
          sold_percent: null,
          proceeds: null,
          sale_ref: uuid(),
        };
      }
      return { ...preEmptyRow, amount: null };
    }
    return {
      ...preEmptyRow,
      shares: null,
      cost_basis: null,
      proceeds: null,
      sale_ref: uuid(),
    };
  }, [modeledAfterConvertibleNote, isLoanNote]);

  const disabledCondition = useMemo(
    () => isDisabled || isEmpty(acquisitionsData?.acquisitions),
    [isDisabled, acquisitionsData]
  );

  const { cells } = useContext(FeaturedSpreadsheetContext);
  const [rows, setRows] = useState<ShareSaleReportRow[]>([]);

  // FOD parents inherit the company's captable_currency as the default Pre-FX
  // source currency. See PurchasedTable for the same lookup pattern.
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const defaultPreFxCurrency = companyInfo?.captable_currency as string | undefined;

  // Live mirror of the soldCells map; the Pre-FX preview reads each row's
  // sale_date from this ref on every preview attempt instead of capturing a
  // (potentially stale) snapshot at parse-cells time.
  const soldCellsRef = useRef(soldCells);
  useEffect(() => {
    soldCellsRef.current = soldCells;
  }, [soldCells]);

  // Pre-FX backend field alias is "proceeds" (see SHARES_SALE_PRE_FX_FIELD_ALIAS).
  // The icon belongs only on rows where the host PROCEEDS cell exists — that's
  // regular sales and loan-note sales, NOT non-loan convertible-note sales
  // (those use AMOUNT and have no PROCEEDS cell).
  const showsProceedsCell = !modeledAfterConvertibleNote || isLoanNote;

  const cellKeys = useMemo(() => {
    if (modeledAfterConvertibleNote) {
      if (isLoanNote) {
        return [PROCEEDS_ALIAS, SOLD_PERCENT_ALIAS];
      }
      return [AMOUNT_ALIAS];
    }
    return [PROCEEDS_ALIAS, COST_BASIS_ALIAS, SHARES_ALIAS];
  }, [modeledAfterConvertibleNote, isLoanNote]);

  const { linkedCellChanges, exchangeRateMap } = useExchangeRateMap({
    fundCurrency,
    format,
    shares: rows,
    selectedMeasurementDate,
    isDifferentCurrency,
    cellKeys,
    dateProp: SALE_DATE_ALIAS,
  });

  const addRow = useCallback(() => {
    // this needs to update the acquisitionsData so it re renders top down
    // sales data must be inside an acquisitions data, set to the newest
    if (isEmpty(acquisitionsData?.acquisitions)) {
      return;
    }
    const newestAcquisition = reverseParsedAcquisitions.reduce(
      (acc, curr) => (acc.purchase_date > curr.purchase_date ? acc : curr),
      {} as CashDistributionLedgerAcquisition
    );
    const newRow = {
      ...emptyRow,
      acquisition_ref: newestAcquisition.acquisition_ref,
      purchase_date: newestAcquisition.purchase_date,
    };
    newestAcquisition.sales?.push(newRow);
    const insertionIndex = acquisitionsData?.acquisitions.findIndex(
      acq => acq.acquisition_ref === newestAcquisition.acquisition_ref
    );
    const updatedAcquisitions = [...reverseParsedAcquisitions];
    updatedAcquisitions[insertionIndex] = newestAcquisition;
    setAcquisitionsData({ ...acquisitionsData, acquisitions: updatedAcquisitions });
  }, [acquisitionsData, setAcquisitionsData, reverseParsedAcquisitions, emptyRow]);

  const deleteRow = useCallback(
    rowIndex => {
      if (!rows) {
        return;
      }
      // find the acquisition the share sale belongs to
      const updatedAcquisitionsData = { ...acquisitionsData };
      const acquisitionRef = rows[rowIndex].acquisition_ref;
      const acquisition = updatedAcquisitionsData?.acquisitions.find(acq => acq.acquisition_ref === acquisitionRef);
      // find the sale in the acquisition and then remove it
      const saleIndex = acquisition?.sales.findIndex(sale => sale.sale_ref === rows[rowIndex].sale_ref) ?? -1;

      if (acquisition?.sales[saleIndex]?.id) {
        acquisition.deleted_sales?.push(acquisition.sales[saleIndex].id);
      }
      acquisition?.sales.splice(saleIndex, 1);
      setAcquisitionsData(updatedAcquisitionsData);
    },
    [acquisitionsData, setAcquisitionsData, rows]
  );

  const config = useMemo(
    () => colConfig(modeledAfterConvertibleNote, cell.isCustomSecurity, !!isLoanNote, acquisitionsData?.acquisitions, sharesMaxDecimalPlaces),
    [modeledAfterConvertibleNote, cell.isCustomSecurity, acquisitionsData, isLoanNote, sharesMaxDecimalPlaces]
  );

  // Set Rows
  useEffect(() => {
    if (cells && acquisitionsData?.acquisitions) {
      if (isEmpty(acquisitionsData?.acquisitions)) {
        setRows([]);
      } else if (modeledAfterConvertibleNote && acquisitionsData?.acquisitions.length) {
        const sales = acquisitionsData?.acquisitions.map(acq => acq.sales).flat();
        const fromExistingSold: ShareSaleReportRow[] = sales.map(row => {
          const purchase_date =
            acquisitionsData?.acquisitions.find(acq => acq.acquisition_ref === row.acquisition_ref)?.purchase_date ??
            '';
          const commonValues = {
            purchase_date,
            sale_date: row.sale_date,
            acquisition_ref: row.acquisition_ref,
            sale_ref: row.sale_ref,
            id: row.id ?? 0,
          };

          if (isLoanNote) {
            return {
              ...commonValues,
              sold_percent: row.sold_percent,
              proceeds: row.proceeds ?? null,
            };
          }
          return {
            ...commonValues,
            amount: row.cost_basis ?? row.amount,
          };
        });
        setRows(fromExistingSold || []);
      } else {
        const sales = acquisitionsData?.acquisitions.map(acq => acq.sales).flat();
        const salesWithReferences = sales.map(row => ({
          purchase_date:
            acquisitionsData?.acquisitions.find(acq => acq.acquisition_ref === row.acquisition_ref)?.purchase_date ??
            '',
          sale_date: row.sale_date,
          cost_basis: row.cost_basis ?? null,
          shares: row.shares ?? null,
          proceeds: row.proceeds ?? null,
          acquisition_ref: row.acquisition_ref,
          sale_ref: row.sale_ref,
          id: row.id ?? 0,
        }));
        setRows(salesWithReferences || []);
      }
    }
  }, [cells, acquisitionsData, isLoanNote, modeledAfterConvertibleNote]);

  // Parse cells
  useEffect(() => {
    if (Array.isArray(rows)) {
      const parsedCells = cellsParser(rows, config) as Record<string, ICell & { relatedDate: string }>;
      Object.values(parsedCells).forEach((parsedCell: ICell & { relatedDate: string }) => {
        if (cellKeys.includes(parsedCell.alias) && isDifferentCurrency) {
          parsedCell.relatedDate = parsedCells[`sale_date${parsedCell.rowNumber! + 1}`].value as string;
        }
      });
      // Attach Pre-FX annotation viewer to PROCEEDS cells where they exist.
      let outCells: Record<string, ICell & { relatedDate: string }> = parsedCells;
      if (showsProceedsCell) {
        outCells = Object.entries(parsedCells).reduce((acc, [key, parsedCell]) => {
          if (parsedCell.alias === PROCEEDS_ALIAS) {
            const sale = rows[parsedCell.rowNumber!];
            // Sales live nested inside `acquisitionsData.acquisitions[i].sales[j]`;
            // patch by `sale_ref` (stable client UUID) across the nested
            // structure so the proceeds cell sees the converted value
            // immediately when the popout preview resolves.
            const saleRowNumber = parsedCell.rowNumber!;
            // Sale date getter: read from the live soldCells map so a
            // freshly-typed date is visible to the preview before the
            // reverse-parse cycle syncs it back to acquisitionsData.
            const getSaleDate = (): string | undefined | null => {
              const dateCell = soldCellsRef.current?.[`${SALE_DATE_ALIAS}${saleRowNumber + 1}`] as
                | { value?: string | null }
                | undefined;
              const fromCells = (dateCell?.value as string | null | undefined) ?? undefined;
              return fromCells ?? (sale?.sale_date as string | null | undefined) ?? undefined;
            };
            // Patch the host proceeds cell DIRECTLY in `soldCells` instead
            // of round-tripping through `setAcquisitionsData`. The
            // acquisitions map lags behind the spreadsheet's live cell
            // state, so going through it would clobber the user's other
            // in-progress cell edits (sale_date, shares, sold_percent).
            const hostCellKey = parsedCell.key;
            const onHostPreview = hostCellKey
              ? (value: number | null) => {
                setSoldCells(prev => ({
                  ...prev,
                  [hostCellKey]: {
                    ...prev[hostCellKey],
                    value,
                    expr: value === null ? '' : String(value),
                  } as FundOwnershipCell,
                }));
              }
              : undefined;
            return {
              ...acc,
              [key]: applyPreFxAnnotation({
                cell: parsedCell,
                parentType: 'sharessale',
                parentId: sale?.id,
                parentRef: sale?.sale_ref ?? null,
                fieldAlias: PROCEEDS_ALIAS,
                defaultCurrency: defaultPreFxCurrency,
                hostCurrency: defaultPreFxCurrency,
                transactionDate: getSaleDate,
                onHostPreview,
              }) as ICell & { relatedDate: string },
            };
          }
          return { ...acc, [key]: parsedCell };
        }, {} as Record<string, ICell & { relatedDate: string }>);
      }
      setSoldCells(outCells as Record<string, FundOwnershipCell>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isDifferentCurrency, setSoldCells, cellKeys, showsProceedsCell, defaultPreFxCurrency]);

  return (
    <CustomCompactContainer direction="column" rowSpacing={2.5} wrap="nowrap">
      <Grid>
        <h4>Sold</h4>
      </Grid>

      <Grid>
        <LedgerTable
          colConfig={config}
          rows={rows}
          tableTerms={{
            tableName: 'Sold Shares',
            tableSlug: 'sold-table',
            columnName: `${modeledAfterConvertibleNote ? 'Note' : 'Shares'}`,
            pluralColumnName: 'Sold Shares',
          }}
          disabled={disabledCondition}
          addRow={addRow}
          deleteRow={deleteRow}
          cells={soldCells}
          setCells={setSoldCells}
          doFullValidation={doFullValidation}
          setIsValid={setIsValid}
          updateValidationStatus={updateValidationStatus}
          tableData={{ acquisitions: reverseParsedAcquisitions }}
          conditions={conditions}
          currency={format.currency}
          linkedCellUpdates={linkedCellChanges}
          exchangeRateMap={exchangeRateMap}
        />
      </Grid>
    </CustomCompactContainer>
  );
};

export default SoldTable;
